import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Shield, RefreshCw, LogOut, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PDPAConsentModal({ user, onAcknowledged, onLogout }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pdpaStatus, setPdpaStatus] = useState(null);
  const [agreedClauses, setAgreedClauses] = useState([]);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    if (user) {
      checkPDPAStatus();
    }
  }, [user]);

  const checkPDPAStatus = async () => {
    try {
      const response = await axios.get(`${API}/pdpa/my-status`, { withCredentials: true });
      setPdpaStatus(response.data);
      
      // Pre-check non-required clauses if any
      if (response.data.pdpa_settings?.clauses) {
        const nonRequired = response.data.pdpa_settings.clauses
          .filter(c => !c.required)
          .map(c => c.id);
        // Don't pre-check anything, let user decide
      }
    } catch (error) {
      console.error("Error checking PDPA status:", error);
      // If API fails, don't block the user
      setPdpaStatus({ needs_acknowledgment: false, reason: "api_error" });
    } finally {
      setLoading(false);
    }
  };

  const toggleClause = (clauseId) => {
    if (agreedClauses.includes(clauseId)) {
      setAgreedClauses(agreedClauses.filter(id => id !== clauseId));
    } else {
      setAgreedClauses([...agreedClauses, clauseId]);
    }
  };

  const handleAgree = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/pdpa/acknowledge`, {
        agreed_clauses: agreedClauses,
        dont_show_again: dontShowAgain
      }, { withCredentials: true });
      
      toast.success("Thank you for acknowledging the terms");
      onAcknowledged();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit acknowledgment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = () => {
    if (window.confirm("If you do not agree to the terms, you will be logged out and cannot use the portal. Are you sure?")) {
      onLogout();
    }
  };

  // Check if all required clauses are agreed
  const allRequiredAgreed = () => {
    if (!pdpaStatus?.pdpa_settings?.clauses) return false;
    const requiredIds = pdpaStatus.pdpa_settings.clauses
      .filter(c => c.required)
      .map(c => c.id);
    return requiredIds.every(id => agreedClauses.includes(id));
  };

  // Loading state
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100]">
        <div className="bg-white rounded-lg p-8 text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Checking consent status...</p>
        </div>
      </div>
    );
  }

  // No acknowledgment needed
  if (!pdpaStatus?.needs_acknowledgment) {
    return null;
  }

  const settings = pdpaStatus.pdpa_settings;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-xl">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8" />
            <div>
              <h2 className="text-xl font-bold">Terms & Consent</h2>
              <p className="text-blue-200 text-sm">
                {pdpaStatus.reason === 'version_changed' 
                  ? 'Our terms have been updated. Please review and acknowledge.'
                  : pdpaStatus.reason === 'force_show'
                  ? 'Please review and acknowledge the terms to continue.'
                  : 'Please review and acknowledge the following to continue.'}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Main Header */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">
              {settings.main_header}
            </p>
          </div>

          {/* Clauses */}
          <div className="space-y-4 mb-6">
            {settings.clauses.map((clause) => (
              <label 
                key={clause.id}
                className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  agreedClauses.includes(clause.id)
                    ? 'border-green-500 bg-green-50'
                    : clause.required
                    ? 'border-red-200 bg-red-50/30 hover:border-red-300'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Checkbox
                  checked={agreedClauses.includes(clause.id)}
                  onCheckedChange={() => toggleClause(clause.id)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="text-gray-700">{clause.text}</p>
                  {clause.required && (
                    <span className="text-xs text-red-500 mt-1 inline-block">* Required</span>
                  )}
                </div>
                {agreedClauses.includes(clause.id) && (
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                )}
              </label>
            ))}
          </div>

          {/* Don't Show Again Option */}
          <div className="border-t border-gray-200 pt-4 mb-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={dontShowAgain}
                onCheckedChange={setDontShowAgain}
              />
              <span className="text-gray-600 text-sm">
                Don't show this again (until terms are updated)
              </span>
            </label>
          </div>

          {/* Progress Indicator */}
          <div className="mb-6">
            {(() => {
              const requiredCount = settings.clauses.filter(c => c.required).length;
              const agreedRequiredCount = settings.clauses.filter(
                c => c.required && agreedClauses.includes(c.id)
              ).length;
              const progress = requiredCount > 0 ? (agreedRequiredCount / requiredCount) * 100 : 100;
              
              return (
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Required acknowledgments</span>
                    <span>{agreedRequiredCount} / {requiredCount}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all ${
                        progress === 100 ? 'bg-green-500' : 'bg-blue-500'
                      }`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleDecline}
              className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              I Do Not Agree (Logout)
            </Button>
            <Button
              onClick={handleAgree}
              disabled={!allRequiredAgreed() || submitting}
              className={`flex-1 ${
                allRequiredAgreed()
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {submitting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  I Agree & Continue
                </>
              )}
            </Button>
          </div>

          {!allRequiredAgreed() && (
            <p className="text-center text-sm text-red-500 mt-4">
              Please check all required boxes to continue
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-xl border-t">
          <p className="text-xs text-gray-500 text-center">
            Version {settings.version} • Your acknowledgment will be recorded for compliance purposes
          </p>
        </div>
      </div>
    </div>
  );
}
